import { RegisterScreen } from '@/components/RegisterScreen';
import { formatCurrency, formatDate, formatNumber } from '@/components/live';
import { ActivityLabour } from '@/screens/parts/ActivityLabour';
import { doneByFields, usePracticePickers } from '@/screens/parts/practiceFields';

interface Row {
  id: string; harvest_code: string | null; plot_id: string; plot_code: string | null; farmer_name: string | null; farmer_code: string | null;
  species_label: string | null; harvest_date: string; felling_started_on: string | null; felling_completed_on: string | null;
  done_by_type: string | null; done_by_contractor_id: string | null; done_by_display: string | null;
  workers_engaged: number | null; person_days: number | null; labour_cost: number | null;
  labour_workers_paid: number; labour_days_paid: number; labour_paid: number; labour_due: number;
}

/**
 * Who felled, logged and loaded each harvest, and what they were paid (owner, 2026-09-27: "Harvest
 * activity workers - summary page as similar to silviculture"). The same rows as the Harvest
 * report, read for the crew: the headcount and person-days on the harvest, and each worker's pay as a
 * worker payment tied to it, on the Labour tab - which also shows on the worker's own record.
 */
export default function HarvestLabourScreen() {
  const pick = usePracticePickers();
  return (
    <RegisterScreen<Row>
      plotFilter canAdd={false}
      extraTab={(row) => ({
        label: 'Labour',
        content: <ActivityLabour activity={{ id: row.id, plot_id: row.plot_id, started_on: row.felling_started_on ?? row.harvest_date,
          completed_on: row.felling_completed_on, done_by_type: row.done_by_type, done_by_contractor_id: row.done_by_contractor_id,
          done_by_contractor_name: row.done_by_type === 'contractor' ? row.done_by_display : null }} link="harvest_id" />,
      })}
      addLabel="" exportName="harvest-labour" recordNoun="harvest"
      orderBy={{ column: 'harvest_date', ascending: false }}
      recordLabel={(row) => `harvest ${row.harvest_code ?? ''} - crew`}
      screenKey="plot.harvest"
      searchColumns={['harvest_code', 'plot_code', 'farmer_name', 'farmer_code', 'done_by_name']}
      secondary={{ sum: (row) => Number(row.person_days ?? row.labour_days_paid ?? 0), unit: 'person-days', digits: 1 }}
      subtitle="The crew on each harvest - felling, cross-cutting, stacking, loading - their person-days, and each worker's pay. Harvests are recorded on Tree inventory."
      table="ops.plot_harvest_entries" title="Harvest labour" view="plot_harvest_entries"
      columns={[
        { key: 'harvest_code', label: 'Harvest', render: (row) => (
          <span className="cell-stack"><strong>{row.harvest_code ?? '--'}</strong><small className="muted">{formatDate(row.harvest_date)}</small></span>
        ) },
        { key: 'farmer_name', label: 'Farmer', render: (row) => (
          <span className="cell-stack"><strong>{row.farmer_name ?? '--'}</strong>
            <small className="muted">{[row.farmer_code, row.plot_code].filter(Boolean).join(' · ')}</small></span>
        ) },
        { key: 'done_by_display', label: 'Done by', render: (row) => row.done_by_display ?? <span className="field-error">not recorded</span> },
        { key: 'workers_engaged', label: 'Workers', align: 'right', render: (row) => formatNumber(row.workers_engaged ?? row.labour_workers_paid, 0) },
        { key: 'person_days', label: 'Person-days', align: 'right', render: (row) => formatNumber(row.person_days ?? row.labour_days_paid, 1) },
        { key: 'labour_cost', label: 'Cost', align: 'right', render: (row) => (row.labour_cost == null ? '--' : formatCurrency(row.labour_cost)) },
        { key: 'labour_paid', label: 'Paid to workers', align: 'right', render: (row) => formatCurrency(row.labour_paid) },
        { key: 'labour_due', label: 'Still due', align: 'right', render: (row) => (row.labour_due > 0
          ? <span className="field-error">{formatCurrency(row.labour_due)}</span> : '--') },
      ]}
      stats={[
        { key: 'no_crew', label: 'Crew not recorded', tone: 'warn', match: (row) => !row.done_by_type },
        { key: 'unpaid', label: 'Pay still due', tone: 'error', match: (row) => row.labour_due > 0 },
        { key: 'contracted', label: 'By a contractor', tone: 'neutral', match: (row) => row.done_by_type === 'contractor' },
        { key: 'no_pay', label: 'No worker paid yet', tone: 'info', match: (row) => row.done_by_type === 'worker' || row.done_by_type === 'contractor' ? row.labour_workers_paid === 0 : false },
      ]}
      fields={[
        ...doneByFields('done_by', pick.workers, pick.contractors).map((field, at) => (at === 0 ? { ...field, label: 'Felled by', section: 'Crew' } : field)),
        { key: 'workers_engaged', label: 'Workers engaged', type: 'number' },
        { key: 'person_days', label: 'Person-days', type: 'number' },
        { key: 'labour_cost', label: 'Labour cost (₹)', type: 'number', hint: 'The whole crew\'s cost, including a contractor\'s bill. Each worker\'s own pay goes on the Labour tab.' },
      ]}
      validate={(form) => ({
        workers_engaged: form.workers_engaged !== '' && form.workers_engaged != null && !(Number(form.workers_engaged) > 0) ? 'More than 0.' : undefined,
        person_days: form.person_days !== '' && form.person_days != null && !(Number(form.person_days) > 0) ? 'More than 0.' : undefined,
      })}
    />
  );
}
